import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Upload, X, Save, MoveUp, MoveDown } from 'lucide-react';

const BoardMembersManager = () => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [message, setMessage] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    order: 0,
    is_active: true
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/admin/board-members`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || []);
      }
    } catch (error) {
      console.error('Erreur chargement membres:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      description: '',
      order: members.length,
      is_active: true
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setEditingMember(null);
    setIsAddingNew(false);
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setIsAddingNew(false);
    setFormData({
      name: member.name,
      title: member.title,
      description: member.description || '',
      order: member.order,
      is_active: member.is_active
    });
    setPhotoPreview(member.photo_url || null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('authToken');

      // Créer ou mettre à jour le membre
      const url = editingMember
        ? `${API_BASE}/api/admin/board-members/${editingMember.id}`
        : `${API_BASE}/api/admin/board-members`;

      const method = editingMember ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        const memberId = editingMember ? editingMember.id : data.id;

        // Upload photo si présente
        if (photoFile) {
          const formData = new FormData();
          formData.append('file', photoFile);

          await fetch(`${API_BASE}/api/admin/board-members/${memberId}/upload-photo`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            body: formData,
          });
        }

        setMessage({ type: 'success', text: editingMember ? 'Membre mis à jour' : 'Membre ajouté' });
        fetchMembers();
        resetForm();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur de connexion' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (memberId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/admin/board-members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Membre supprimé' });
        fetchMembers();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    }
  };

  const handleDeletePhoto = async (memberId) => {
    if (!confirm('Supprimer la photo ?')) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/admin/board-members/${memberId}/photo`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Photo supprimée' });
        fetchMembers();
        if (editingMember && editingMember.id === memberId) {
          setPhotoPreview(null);
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression de la photo' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Messages */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Bouton ajouter */}
      {!isAddingNew && !editingMember && (
        <button
          onClick={() => setIsAddingNew(true)}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg"
          style={{ backgroundColor: 'rgb(196, 150, 96)' }}
        >
          <Plus className="w-4 h-4" />
          Ajouter un membre
        </button>
      )}

      {/* Formulaire */}
      {(isAddingNew || editingMember) && (
        <div className="bg-white rounded-lg border p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold" style={{ color: 'rgb(196, 150, 96)' }}>
              {editingMember ? 'Modifier le membre' : 'Nouveau membre'}
            </h3>
            <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Photo */}
            <div>
              <label className="block text-sm font-medium mb-2">Photo</label>
              <div className="flex items-start gap-4">
                {photoPreview && (
                  <div className="relative">
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg border"
                    />
                    {editingMember && editingMember.photo && (
                      <button
                        type="button"
                        onClick={() => handleDeletePhoto(editingMember.id)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="block"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Format: JPG, PNG, WEBP (max 5MB)
                  </p>
                </div>
              </div>
            </div>

            {/* Nom */}
            <div>
              <label className="block text-sm font-medium mb-2">Nom complet *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ex: Comte Jean-Charles de T'Serclaes"
              />
            </div>

            {/* Fonction */}
            <div>
              <label className="block text-sm font-medium mb-2">Fonction *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ex: Président"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2">Description (optionnel)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows="3"
                placeholder="Informations supplémentaires..."
              />
            </div>

            {/* Ordre */}
            <div>
              <label className="block text-sm font-medium mb-2">Ordre d'affichage</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            {/* Actif */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded"
              />
              <label className="text-sm">Membre actif (visible sur le site)</label>
            </div>

            {/* Boutons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
              >
                <Save className="w-4 h-4" />
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border rounded-lg"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des membres */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map((member) => (
          <div key={member.id} className="bg-white rounded-lg border p-4 hover:shadow-lg transition-shadow">
            {member.photo_url && (
              <img
                src={member.photo_url}
                alt={member.name}
                className="w-full h-48 object-cover rounded-lg mb-3"
              />
            )}
            <h4 className="font-bold text-lg mb-1" style={{ color: 'rgb(196, 150, 96)' }}>
              {member.name}
            </h4>
            <p className="text-gray-600 mb-2">{member.title}</p>
            {member.description && (
              <p className="text-sm text-gray-500 mb-3">{member.description}</p>
            )}
            <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
              <span>Ordre: {member.order}</span>
              <span className={member.is_active ? 'text-green-600' : 'text-red-600'}>
                {member.is_active ? 'Actif' : 'Inactif'}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(member)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50"
              >
                <Edit2 className="w-4 h-4" />
                Modifier
              </button>
              <button
                onClick={() => handleDelete(member.id)}
                className="px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {members.length === 0 && !isAddingNew && (
        <div className="text-center py-12 text-gray-500">
          Aucun membre du conseil. Cliquez sur "Ajouter un membre" pour commencer.
        </div>
      )}
    </div>
  );
};

export default BoardMembersManager;
